/**
 * Kategori senkronizasyonu hook'u
 * URL'den kategori bilgisini okuyup form'a set eder
 */

import { useEffect, useRef, useCallback } from 'react'
import type { SearchField } from '@/types/product-search'

export interface UseCategorySyncReturn {
    categoryLoadedRef: React.MutableRefObject<string | null>
}

/**
 * URL'den kategori sayfası kontrolü
 */
function isCategoryPageFromUrl(pathname: string | null): { isCategory: boolean; slug: string | null } {
    if (!pathname) return { isCategory: false, slug: null }
    
    const segments = pathname.split('/').filter(Boolean)
    if (segments.length === 0) return { isCategory: false, slug: null }
    
    const isLangCode = segments[0]?.length === 2 && /^[a-z]{2}$/.test(segments[0])
    
    if (isLangCode) {
        if (segments.length === 3) {
            return { isCategory: true, slug: segments[2] }
        }
        return { isCategory: false, slug: null }
    }
    
    if (segments.length === 2) {
        return { isCategory: true, slug: segments[1] }
    }
    
    return { isCategory: false, slug: null }
}

export function useCategorySync(
    pathname: string | null,
    categorySlug: string | undefined,
    categoryName: string | undefined,
    activeSearchFields: SearchField[],
    selectedSuggestionSlugs: Record<string, string>,
    setCategoryToForm: (fieldKey: string, categorySlug: string, categoryName?: string) => void
): UseCategorySyncReturn {
    const categoryLoadedRef = useRef<string | null>(null)
    
    useEffect(() => {
        const { isCategory, slug: urlSlug } = isCategoryPageFromUrl(pathname)
        const finalCategorySlug = categorySlug || urlSlug
        
        if (!isCategory || !finalCategorySlug) {
            if (categoryLoadedRef.current) {
                categoryLoadedRef.current = null
            }
            return
        }
        
        if (categoryLoadedRef.current === finalCategorySlug) return
        if (!activeSearchFields?.length) return
        
        const autocompleteFields = activeSearchFields.filter(f => f?.field_type === 'autocomplete')
        if (!autocompleteFields.length) return
        
        const firstField = autocompleteFields[0]
        if (selectedSuggestionSlugs[firstField.field_key] === finalCategorySlug) {
            categoryLoadedRef.current = finalCategorySlug
            return
        }
        
        const finalCategoryName = categoryName || finalCategorySlug
        setCategoryToForm(firstField.field_key, finalCategorySlug, finalCategoryName)
    }, [categorySlug, categoryName, pathname, activeSearchFields, selectedSuggestionSlugs, setCategoryToForm])
    
    return {
        categoryLoadedRef,
    }
}

